import { Checkbox, Icon, Menu, MenuItem, Tooltip } from "@mui/material";
import MDBox from "components/MDBox";
import UiTypography from "components/base/UiTypography";
import AlwaysEditableField from "components/InlineEditableField/AlwaysEditableField";
import InlineEditableField from "components/InlineEditableField/InlineEditableField";
import ToggleProposalProductOptionality from "modules/proposals/ToggleProposalProductOptionality";
import { ProposalProduct, ProposalStage } from "generated/graphql";
import formatCentsToUSD from "utils/money/formatCentsToUSD";
import getJobSizeUnitLabel from "utils/proposals/getJobSizeUnitLabel";
import {
  validateCurrency,
  validateInteger,
  validatePercentage,
} from "modules/proposals/validators";
import { Fragment, memo, useCallback, useMemo, useState } from "react";
import ExpandedProduct from "./ExpandedProduct";
import { useDraftContext } from "./DraftContext";
import useToggleProposalProductGroupOptionality from "hooks/proposals/useToggleProposalProductGroupOptionality";
import { draftColors } from "./draftDesignTokens";
import { buildStageState, groupAggregates } from "./stageOrderingState";

const productGridTemplate =
  "32px minmax(140px, 2fr) minmax(60px, 100px) 64px 80px minmax(60px, 100px) minmax(60px, 100px) 80px minmax(60px, 100px) 56px 56px";

const metricColumns = [
  { key: "jobSize", label: "Job Size" },
  { key: "margin", label: "Margin" },
  { key: "unitPrice", label: "Unit Price" },
  { key: "cost", label: "Cost" },
  { key: "profit", label: "Profit" },
  { key: "discount", label: "Discount" },
  { key: "total", label: "Total" },
] as const;

function formatJobSize(value: string | number): string {
  const num = typeof value === "string" ? parseInt(value, 10) : value;
  if (isNaN(num) || num === 0) return "0";
  return num.toLocaleString("en-US");
}

const parseCurrencyToCents = (value: string) => {
  if (value.trim() === "") return "";
  const num = parseFloat(value.replace(/[^0-9.-]/g, ""));
  return isNaN(num) ? "" : String(num);
};

type StageProductTableProps = {
  products: ProposalProduct[];
  stage: ProposalStage;
  expandedProductId: string | false;
  onToggleExpand: (id: string) => void;
  onDeleteProduct: (id: string) => void;
  onCopyProduct: (productId: string, targetStageId: string) => Promise<void>;
  availableStages: ProposalStage[];
  onUpdateName: (id: string, value: string) => Promise<void>;
  onUpdateProfit: (id: string, value: string) => Promise<void>;
  onUpdateMargin: (id: string, value: string) => Promise<void>;
  onUpdateTotal: (id: string, value: string) => Promise<void>;
  onUpdateUnitPrice: (id: string, value: string) => Promise<void>;
  onUpdateJobSize: (id: string, value: string) => Promise<void>;
  onUpdateDiscount: (id: string, value: string) => Promise<void>;
};

function getProductDisplayValues(product: ProposalProduct) {
  const productName =
    product.material?.externalProductName || product.material?.name || "Unnamed Product";
  const isHeaderUnitPriceProduct =
    !!product.material?.unitPriceProduct || !!product.material?.subcontracted;
  const jobSize = product.jobSize ? String(product.jobSize) : "0";
  const profitMargin = product.netMarginPercentage ?? 0;
  const effectiveTotal = Number(
    product.overallTotalOverride ?? product.overallTotalAfterDiscount ?? product.overallTotal ?? 0
  );
  const costTotal = +(product.costTotal ?? 0);
  const isClampedToCost = effectiveTotal < costTotal;
  const overallTotal = isClampedToCost ? costTotal : effectiveTotal;
  const profit = isClampedToCost
    ? "0"
    : product.profitAfterDiscount ?? product.profitOverride ?? product.profit ?? "0";
  const calculatedUnitPrice = product.jobSize ? overallTotal / product.jobSize : 0;
  const effectiveUnitPriceOverride = isHeaderUnitPriceProduct
    ? product.unitPriceOverride
    : product.material?.unitPriceOverride ?? product.unitPriceOverride;
  const unitPriceIsBelowCost =
    effectiveUnitPriceOverride != null &&
    product.jobSize > 0 &&
    Number(effectiveUnitPriceOverride) * product.jobSize < costTotal;
  const unitPrice = isClampedToCost || unitPriceIsBelowCost || isHeaderUnitPriceProduct
    ? Number(calculatedUnitPrice)
    : Number(effectiveUnitPriceOverride ?? calculatedUnitPrice);
  const cost = product.costTotal ?? "0";

  return {
    productName,
    jobSize,
    profitMargin,
    profit,
    totalPrice: String(overallTotal),
    unitPrice,
    cost,
  };
}

type CollapsedProductRowProps = {
  product: ProposalProduct;
  onUpdateName: (id: string, value: string) => Promise<void>;
  onUpdateProfit: (id: string, value: string) => Promise<void>;
  onUpdateMargin: (id: string, value: string) => Promise<void>;
  onUpdateTotal: (id: string, value: string) => Promise<void>;
  onUpdateUnitPrice: (id: string, value: string) => Promise<void>;
  onUpdateJobSize: (id: string, value: string) => Promise<void>;
  onUpdateDiscount: (id: string, value: string) => Promise<void>;
};

function CollapsedProductRow({
  product,
  onUpdateName,
  onUpdateProfit,
  onUpdateMargin,
  onUpdateTotal,
  onUpdateUnitPrice,
  onUpdateJobSize,
  onUpdateDiscount,
}: CollapsedProductRowProps) {
  const { isReadOnly } = useDraftContext();
  const { productName, jobSize, profitMargin, profit, totalPrice, unitPrice, cost } =
    getProductDisplayValues(product);
  const saveTotal = async (value: string) => {
    if (value.trim() === "") {
      await onUpdateTotal(product.id, value);
      return value;
    }

    const minimumTotal = Number(product.costTotal ?? 0) / 100;
    const normalizedTotal = Math.max(Number(value), minimumTotal).toFixed(2);
    await onUpdateTotal(product.id, normalizedTotal);
    return normalizedTotal;
  };

  const saveUnitPrice = async (value: string) => {
    if (value.trim() === "") {
      await onUpdateUnitPrice(product.id, value);
      return value;
    }

    const minimumUnitPrice = product.jobSize > 0
      ? Number(product.costTotal ?? 0) / product.jobSize / 100
      : 0;
    const normalizedUnitPrice = Math.max(Number(value), minimumUnitPrice).toFixed(2);
    await onUpdateUnitPrice(product.id, normalizedUnitPrice);
    return normalizedUnitPrice;
  };

  const cellMap: Record<
    (typeof metricColumns)[number]["key"],
    {
      value: string;
      onSave?: (v: string) => Promise<string | void>;
      format?: (v: string) => string;
      parse?: (v: string) => string;
      validate?: (v: string) => boolean;
      type?: "text" | "currency" | "percentage";
    }
  > = {
    jobSize: {
      value: jobSize,
      onSave: isReadOnly ? undefined : (v) => onUpdateJobSize(product.id, v),
      format: (v) => formatJobSize(v),
      validate: validateInteger,
    },
    margin: {
      value: String(profitMargin),
      onSave: isReadOnly ? undefined : (v) => onUpdateMargin(product.id, v),
      format: (v) => `${v}%`,
      validate: validatePercentage,
      type: "percentage",
    },
    unitPrice: {
      value: (unitPrice / 100).toFixed(2),
      onSave: isReadOnly ? undefined : saveUnitPrice,
      format: (v) => formatCentsToUSD(parseFloat(v) * 100),
      parse: parseCurrencyToCents,
      validate: validateCurrency,
      type: "currency",
    },
    cost: { value: formatCentsToUSD(parseFloat(cost)) },
    profit: {
      value: (parseFloat(profit) / 100).toFixed(2),
      onSave: isReadOnly ? undefined : (v) => onUpdateProfit(product.id, v),
      format: (v) => formatCentsToUSD(parseFloat(v) * 100),
      parse: parseCurrencyToCents,
      validate: validateCurrency,
      type: "currency",
    },
    discount: {
      value: (parseFloat(String(product.discount ?? "0")) / 100).toFixed(2),
      onSave: isReadOnly ? undefined : (v) => onUpdateDiscount(product.id, v),
      format: (v) => formatCentsToUSD(parseFloat(v) * 100),
      parse: parseCurrencyToCents,
      validate: (v) => {
        if (!validateCurrency(v)) return false;
        const discountCents = parseFloat(v) * 100;
        const profitCents = parseFloat(profit);
        return discountCents <= profitCents;
      },
      type: "currency",
    },
    total: {
      value: (parseFloat(totalPrice) / 100).toFixed(2),
      onSave: isReadOnly ? undefined : saveTotal,
      format: (v) => formatCentsToUSD(parseFloat(v) * 100),
      parse: parseCurrencyToCents,
      validate: validateCurrency,
      type: "currency",
    },
  };

  const isSubcontracted = product.material?.subcontracted;
  const isUnitPriced = product.material?.unitPriceProduct;
  const typeLabel = isSubcontracted ? "Subcontracted" : isUnitPriced ? "Unit priced" : null;

  return (
    <>
      <MDBox px="4px" sx={{ minWidth: 0 }}>
        <InlineEditableField
          value={productName}
          onSave={(v) => onUpdateName(product.id, v)}
          disabled={isReadOnly}
          fontSize="12px"
        />
        {typeLabel && (
          <UiTypography
            uiVariant="body-sm"
            sx={{
              fontSize: "10px",
              fontWeight: 600,
              color: draftColors.textSecondary,
              lineHeight: 1.2,
              mt: "2px",
            }}
          >
            [{typeLabel}]
          </UiTypography>
        )}
      </MDBox>
      {metricColumns.map((col) => {
        const cell = cellMap[col.key];
        const measureUnit = col.key === "jobSize" ? getJobSizeUnitLabel(product) : "";
        return (
          <MDBox
            key={col.key}
            px="4px"
            className={col.key === "jobSize" ? "tour-job-size" : undefined}
          >
            {cell.onSave ? (
              <MDBox display="flex" alignItems="center" gap="2px">
                <AlwaysEditableField
                  value={cell.value}
                  onSave={cell.onSave}
                  formatDisplay={cell.format}
                  parseInput={cell.parse}
                  validate={cell.validate}
                  type={cell.type}
                  fontSize="12px"
                />
                {measureUnit && (
                  <MDBox
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    sx={{ lineHeight: 1, flexShrink: 0 }}
                  >
                    {measureUnit.split(" ").map((word, wi) => (
                      <UiTypography
                        key={wi}
                        uiVariant="body-sm"
                        sx={{
                          fontSize: "9px",
                          fontWeight: 600,
                          color: draftColors.textSecondary,
                          lineHeight: 1.1,
                        }}
                      >
                        {word}
                      </UiTypography>
                    ))}
                  </MDBox>
                )}
              </MDBox>
            ) : (
              <UiTypography uiVariant="body-sm" sx={{ textAlign: "right" }}>
                {cell.value}
              </UiTypography>
            )}
          </MDBox>
        );
      })}
    </>
  );
}

const MemoCollapsedProductRow = memo(CollapsedProductRow);

type ProductRowProps = {
  product: ProposalProduct;
  stage: ProposalStage;
  availableStages: ProposalStage[];
  expandedProductId: string | false;
  hideOptional?: boolean;
  onToggleExpand: (id: string) => void;
  onDeleteProduct: (id: string) => void;
  onCopyProduct: (productId: string, targetStageId: string) => Promise<void>;
  onUpdateName: (id: string, value: string) => Promise<void>;
  onUpdateProfit: (id: string, value: string) => Promise<void>;
  onUpdateMargin: (id: string, value: string) => Promise<void>;
  onUpdateTotal: (id: string, value: string) => Promise<void>;
  onUpdateUnitPrice: (id: string, value: string) => Promise<void>;
  onUpdateJobSize: (id: string, value: string) => Promise<void>;
  onUpdateDiscount: (id: string, value: string) => Promise<void>;
};

function ProductRow({
  product,
  stage,
  availableStages,
  expandedProductId,
  hideOptional,
  onToggleExpand,
  onDeleteProduct,
  onCopyProduct,
  onUpdateName,
  onUpdateProfit,
  onUpdateMargin,
  onUpdateTotal,
  onUpdateUnitPrice,
  onUpdateJobSize,
  onUpdateDiscount,
}: ProductRowProps) {
  const isExpanded = expandedProductId === product.id;
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const stageOptions = useMemo(
    () => [
      { id: stage.id, label: `${stage.name || "Current job"} (Duplicate here)` },
      ...availableStages
        .filter((proposalStage) => proposalStage.id !== stage.id)
        .map((proposalStage) => ({
          id: proposalStage.id,
          label: proposalStage.name || `Job ${proposalStage.id}`,
        })),
    ],
    [availableStages, stage.id, stage.name]
  );

  return (
    <Fragment key={product.id}>
      <MDBox
        display="grid"
        gridTemplateColumns={productGridTemplate}
        alignItems="center"
        sx={{
          borderBottom: `1px solid ${draftColors.border}`,
          py: "4px",
          "&:hover": { backgroundColor: "#fafbfc" },
        }}
      >
        <MDBox
          className="tour-product-expand"
          sx={{
            cursor: "pointer",
            color: draftColors.textAction,
            display: "flex",
            justifyContent: "center",
          }}
          onClick={() => onToggleExpand(product.id)}
        >
          <Icon fontSize="small">{isExpanded ? "arrow_drop_down" : "chevron_right"}</Icon>
        </MDBox>

        <MemoCollapsedProductRow
          product={product}
          onUpdateName={onUpdateName}
          onUpdateProfit={onUpdateProfit}
          onUpdateMargin={onUpdateMargin}
          onUpdateTotal={onUpdateTotal}
          onUpdateUnitPrice={onUpdateUnitPrice}
          onUpdateJobSize={onUpdateJobSize}
          onUpdateDiscount={onUpdateDiscount}
        />

        <MDBox sx={{ display: "flex", justifyContent: "center" }}>
          {hideOptional ? (
            <MDBox />
          ) : (
            <ToggleProposalProductOptionality id={product.id} isOptional={product.isOptional} />
          )}
        </MDBox>

        <MDBox display="flex" justifyContent="center" alignItems="center" gap="4px" pr="4px">
          <Tooltip title="Copy Product to Job">
            <MDBox
              sx={{
                display: "flex",
                justifyContent: "center",
                color: draftColors.textAction,
                cursor: "pointer",
              }}
              onClick={(event: React.MouseEvent<HTMLElement>) => setMenuAnchor(event.currentTarget)}
            >
              <Icon fontSize="small">content_copy</Icon>
            </MDBox>
          </Tooltip>
          <MDBox
            sx={{
              display: "flex",
              justifyContent: "center",
              color: draftColors.red,
              cursor: "pointer",
            }}
            onClick={() => onDeleteProduct(product.id)}
          >
            <Icon fontSize="small">delete</Icon>
          </MDBox>
        </MDBox>
      </MDBox>

      {isExpanded && (
        <ExpandedProduct product={product} stage={stage} expandedProductId={expandedProductId} />
      )}

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MDBox
          sx={{
            color: draftColors.textPrimary,
            fontWeight: 700,
            px: 2,
            py: 1,
          }}
        >
          Choose Job to Copy Product:
        </MDBox>
        {stageOptions.map((stageOption) => (
          <MenuItem
            key={stageOption.id}
            onClick={async () => {
              setMenuAnchor(null);
              await onCopyProduct(product.id, stageOption.id);
            }}
          >
            {stageOption.label}
          </MenuItem>
        ))}
      </Menu>
    </Fragment>
  );
}

const MemoProductRow = memo(ProductRow);
export default function StageProductTable({
  products,
  stage,
  expandedProductId,
  onToggleExpand,
  onDeleteProduct,
  onCopyProduct,
  availableStages,
  onUpdateName,
  onUpdateProfit,
  onUpdateMargin,
  onUpdateTotal,
  onUpdateUnitPrice,
  onUpdateJobSize,
  onUpdateDiscount,
}: StageProductTableProps) {
  const { isReadOnly } = useDraftContext();
  const stageState = useMemo(() => buildStageState(stage), [stage]);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [toggleGroupOptionality] = useToggleProposalProductGroupOptionality();

  const toggleGroupCollapse = (groupKey: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });
  };

  const handleToggleGroupOptional = useCallback(
    (groupId: string, currentIsOptional: boolean) => {
      toggleGroupOptionality({
        variables: { id: groupId, isOptional: !currentIsOptional },
      });
    },
    [toggleGroupOptionality]
  );

  return (
    <MDBox>
      <MDBox
        display="grid"
        gridTemplateColumns={productGridTemplate}
        alignItems="center"
        sx={{
          backgroundColor: draftColors.surfaceMuted,
          borderTop: `1px solid ${draftColors.border}`,
          borderBottom: `1px solid ${draftColors.border}`,
          py: "8px",
        }}
      >
        <MDBox />
        <UiTypography uiVariant="overline" sx={{ background: "none", border: "none", px: "4px" }}>
          Product Name
        </UiTypography>
        {metricColumns.map((column) => (
          <UiTypography
            key={column.key}
            uiVariant="overline"
            sx={{ background: "none", border: "none", px: "4px", textAlign: "right" }}
          >
            {column.label}
          </UiTypography>
        ))}
        <UiTypography
          uiVariant="overline"
          sx={{ background: "none", border: "none", px: "4px", textAlign: "center" }}
        >
          Optional
        </UiTypography>
        <MDBox />
      </MDBox>
      {stageState.items.map((item, index) => {
        if (item.kind === "product") {
          return (
            <MemoProductRow
              key={item.product.id}
              product={item.product}
              stage={stage}
              availableStages={availableStages}
              expandedProductId={expandedProductId}
              onToggleExpand={onToggleExpand}
              onDeleteProduct={onDeleteProduct}
              onCopyProduct={onCopyProduct}
              onUpdateName={onUpdateName}
              onUpdateProfit={onUpdateProfit}
              onUpdateMargin={onUpdateMargin}
              onUpdateTotal={onUpdateTotal}
              onUpdateUnitPrice={onUpdateUnitPrice}
              onUpdateJobSize={onUpdateJobSize}
              onUpdateDiscount={onUpdateDiscount}
            />
          );
        }

        const { group } = item;
        const groupKey = group.id ?? `group-${index}`;
        const isCollapsed = collapsedGroups.has(groupKey);
        const agg = groupAggregates(group.products);
        return (
          <Fragment key={groupKey}>
            <MDBox
              display="grid"
              gridTemplateColumns={productGridTemplate}
              alignItems="center"
              sx={{
                borderBottom: `1px solid ${draftColors.border}`,
                py: "6px",
                backgroundColor: draftColors.surfaceTableHeader,
              }}
            >
              <MDBox
                sx={{
                  cursor: "pointer",
                  color: draftColors.textAction,
                  display: "flex",
                  justifyContent: "center",
                }}
                onClick={() => toggleGroupCollapse(groupKey)}
              >
                <Icon fontSize="small">{isCollapsed ? "chevron_right" : "expand_more"}</Icon>
              </MDBox>

              <MDBox px="4px" display="flex" alignItems="center" gap="6px">
                <UiTypography
                  uiVariant="body-sm"
                  sx={{ fontWeight: 600, color: draftColors.textPrimary }}
                >
                  {group.name || "Unnamed Group"}
                </UiTypography>
              </MDBox>

              <MDBox />
              <MDBox />
              <MDBox />
              <UiTypography
                uiVariant="body-sm"
                sx={{ textAlign: "right", px: "4px", fontWeight: 600 }}
              >
                {formatCentsToUSD(agg.costTotal)}
              </UiTypography>
              <UiTypography
                uiVariant="body-sm"
                sx={{ textAlign: "right", px: "4px", fontWeight: 600 }}
              >
                {formatCentsToUSD(agg.profit)}
              </UiTypography>
              <UiTypography
                uiVariant="body-sm"
                sx={{ textAlign: "right", px: "4px", fontWeight: 600 }}
              >
                {formatCentsToUSD(agg.discount)}
              </UiTypography>
              <UiTypography
                uiVariant="body-sm"
                sx={{ textAlign: "right", px: "4px", fontWeight: 600 }}
              >
                {formatCentsToUSD(agg.overallTotal)}
              </UiTypography>

              <MDBox sx={{ display: "flex", justifyContent: "center" }}>
                <Checkbox
                  checked={group.isOptional}
                  size="small"
                  sx={{ p: 0 }}
                  onChange={() => {
                    if (group.id) handleToggleGroupOptional(group.id, group.isOptional);
                  }}
                />
              </MDBox>

              <MDBox />
            </MDBox>
            {!isCollapsed &&
              group.products.map((groupProduct) => (
                <MDBox key={groupProduct.id} pl="24px">
                  <MemoProductRow
                    product={groupProduct}
                    stage={stage}
                    availableStages={availableStages}
                    expandedProductId={expandedProductId}
                    hideOptional
                    onToggleExpand={onToggleExpand}
                    onDeleteProduct={onDeleteProduct}
                    onCopyProduct={onCopyProduct}
                    onUpdateName={onUpdateName}
                    onUpdateProfit={onUpdateProfit}
                    onUpdateMargin={onUpdateMargin}
                    onUpdateTotal={onUpdateTotal}
                    onUpdateUnitPrice={onUpdateUnitPrice}
                    onUpdateJobSize={onUpdateJobSize}
                    onUpdateDiscount={onUpdateDiscount}
                  />
                </MDBox>
              ))}
          </Fragment>
        );
      })}
    </MDBox>
  );
}
